import React, { useState, useMemo } from 'react';
import { Box, Button, Typography, Radio, RadioGroup, FormControlLabel, TextField, LinearProgress, Chip } from '@mui/material';
import { Brain, CheckCircle, XCircle, Lightbulb, RotateCcw } from 'lucide-react';
import confetti from 'canvas-confetti';

const HEADING = '#fafafa';
const BODY = '#d4d4d8';
const SECONDARY = '#a1a1aa';
const MUTED = '#71717a';
const ACCENT = '#a78bfa';
const GREEN = '#4ade80';
const GOLD = '#fbbf24';
const CARD_BDR = '1px solid rgba(255,255,255,0.08)';
const CARD_RAD = '14px';

const normalizeAnswer = (val) => String(val || '').trim().toLowerCase().replace(/^[a-d]\)\s*/i, '');

const checkAnswer = (question, answer) => {
  const correct = question.correctAnswer;
  if (question.type === 'multiple_choice') {
    const letter = String(answer).trim().charAt(0).toUpperCase();
    const correctLetter = String(correct).trim().charAt(0).toUpperCase();
    return letter === correctLetter;
  }
  if (question.type === 'true_false') {
    return normalizeAnswer(answer) === normalizeAnswer(correct);
  }
  return normalizeAnswer(answer) === normalizeAnswer(correct)
    || normalizeAnswer(answer).includes(normalizeAnswer(correct))
    || normalizeAnswer(correct).includes(normalizeAnswer(answer));
};

const QuizLesson = ({ quiz, onPass, onAnalytics }) => {
  const questions = quiz?.questions || [];
  const passingScore = quiz?.settings?.passingScore ?? 70;
  const maxAttempts = quiz?.settings?.maxAttempts ?? 3;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showHint, setShowHint] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [results, setResults] = useState(null);

  const current = questions[currentIndex];
  const progress = questions.length ? ((currentIndex + (submitted ? 1 : 0)) / questions.length) * 100 : 0;

  const scorePct = useMemo(() => {
    if (!results) return 0;
    const totalPoints = questions.reduce((s, q) => s + (q.points || 1), 0);
    const earned = results.filter((r) => r.correct).reduce((s, r) => s + (r.points || 1), 0);
    return totalPoints > 0 ? Math.round((earned / totalPoints) * 100) : 0;
  }, [results, questions]);

  const handleSelect = (value) => {
    setAnswers((prev) => ({ ...prev, [currentIndex]: value }));
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((i) => i + 1);
      setShowHint(false);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
      setShowHint(false);
    }
  };

  const handleSubmit = () => {
    const graded = questions.map((q, i) => {
      const userAnswer = answers[i];
      const correct = checkAnswer(q, userAnswer);
      return { correct, points: correct ? (q.points || 1) : 0, userAnswer, question: q };
    });
    const pct = (() => {
      const total = questions.reduce((s, q) => s + (q.points || 1), 0);
      const earned = graded.filter((g) => g.correct).reduce((s, g) => s + g.points, 0);
      return total > 0 ? Math.round((earned / total) * 100) : 0;
    })();

    setResults(graded);
    setSubmitted(true);
    setAttempts((a) => a + 1);
    onAnalytics?.('quiz_attempt', { score: pct, passed: pct >= passingScore });

    if (pct >= passingScore) {
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 }, colors: ['#a78bfa', '#4ade80'] });
      onPass?.(pct);
    }
  };

  const handleRetry = () => {
    if (attempts >= maxAttempts) return;
    setAnswers({});
    setCurrentIndex(0);
    setSubmitted(false);
    setResults(null);
    setShowHint(false);
  };

  if (!questions.length) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: MUTED }}>
        <Typography>No quiz questions available yet.</Typography>
      </Box>
    );
  }

  if (submitted && results) {
    const passed = scorePct >= passingScore;
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, border: CARD_BDR, borderRadius: CARD_RAD, bgcolor: 'rgba(255,255,255,0.02)' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          {passed ? <CheckCircle size={48} color={GREEN} /> : <XCircle size={48} color="#f87171" />}
          <Typography sx={{ color: HEADING, fontWeight: 800, fontSize: 22, mt: 1.5 }}>
            {passed ? 'Quiz Passed!' : 'Keep Practicing'}
          </Typography>
          <Typography sx={{ color: SECONDARY, mt: 0.5 }}>
            Score: {scorePct}% · Need {passingScore}% to pass
          </Typography>
          <Chip
            label={`Attempt ${attempts} of ${maxAttempts}`}
            size="small"
            sx={{ mt: 1.5, bgcolor: 'rgba(167,139,250,0.1)', color: ACCENT }}
          />
        </Box>

        {results.map((r, i) => (
          <Box key={i} sx={{ mb: 2, p: 2, borderRadius: '10px', border: CARD_BDR, bgcolor: r.correct ? 'rgba(74,222,128,0.05)' : 'rgba(248,113,113,0.05)' }}>
            <Typography sx={{ color: HEADING, fontWeight: 600, fontSize: 14, mb: 0.5 }}>
              {i + 1}. {r.question.question}
            </Typography>
            <Typography sx={{ color: r.correct ? GREEN : '#f87171', fontSize: 13 }}>
              {r.correct ? '✓ Correct' : `✗ Your answer: ${r.userAnswer || '(blank)'}`}
            </Typography>
            {r.question.explanation && (
              <Typography sx={{ color: MUTED, fontSize: 12, mt: 1, lineHeight: 1.6 }}>
                {r.question.explanation}
              </Typography>
            )}
          </Box>
        ))}

        {!passed && attempts < maxAttempts && (
          <Button
            startIcon={<RotateCcw size={16} />}
            onClick={handleRetry}
            fullWidth
            sx={{ mt: 2, color: ACCENT, borderColor: 'rgba(167,139,250,0.3)', textTransform: 'none', borderRadius: '10px' }}
            variant="outlined"
          >
            Try Again ({maxAttempts - attempts} left)
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ border: CARD_BDR, borderRadius: CARD_RAD, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.02)' }}>
      <Box sx={{ px: 2.5, py: 2, borderBottom: CARD_BDR, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Brain size={20} color="#f472b6" />
        <Typography sx={{ color: HEADING, fontWeight: 700, fontSize: 15, flex: 1 }}>
          Interactive Quiz
        </Typography>
        <Typography sx={{ color: MUTED, fontSize: 12 }}>
          {currentIndex + 1} / {questions.length}
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{ height: 3, bgcolor: 'rgba(255,255,255,0.04)', '& .MuiLinearProgress-bar': { bgcolor: ACCENT } }}
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography sx={{ color: HEADING, fontWeight: 700, fontSize: 16, mb: 2.5, lineHeight: 1.5 }}>
          {current.question}
        </Typography>

        {current.type === 'multiple_choice' && current.options ? (
          <RadioGroup value={answers[currentIndex] || ''} onChange={(e) => handleSelect(e.target.value)}>
            {current.options.map((opt, i) => (
              <FormControlLabel
                key={i}
                value={opt}
                control={<Radio sx={{ color: MUTED, '&.Mui-checked': { color: ACCENT } }} />}
                label={<Typography sx={{ color: BODY, fontSize: 14 }}>{opt}</Typography>}
                sx={{ mb: 0.5, mx: 0 }}
              />
            ))}
          </RadioGroup>
        ) : current.type === 'true_false' ? (
          <RadioGroup value={answers[currentIndex] || ''} onChange={(e) => handleSelect(e.target.value)}>
            {['True', 'False'].map((opt) => (
              <FormControlLabel
                key={opt}
                value={opt}
                control={<Radio sx={{ color: MUTED, '&.Mui-checked': { color: ACCENT } }} />}
                label={<Typography sx={{ color: BODY, fontSize: 14 }}>{opt}</Typography>}
                sx={{ mb: 0.5, mx: 0 }}
              />
            ))}
          </RadioGroup>
        ) : (
          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder="Type your answer…"
            value={answers[currentIndex] || ''}
            onChange={(e) => handleSelect(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '10px', color: BODY,
                '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(167,139,250,0.4)' },
              },
            }}
          />
        )}

        {current.hints?.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              startIcon={<Lightbulb size={14} />}
              onClick={() => setShowHint((h) => !h)}
              sx={{ color: GOLD, textTransform: 'none' }}
            >
              {showHint ? 'Hide hint' : 'Show hint'}
            </Button>
            {showHint && (
              <Typography sx={{ color: SECONDARY, fontSize: 13, mt: 1, pl: 1, borderLeft: `2px solid ${GOLD}` }}>
                {current.hints[0]}
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, gap: 1 }}>
          <Button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            sx={{ color: SECONDARY, textTransform: 'none' }}
          >
            Previous
          </Button>
          {currentIndex < questions.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={!answers[currentIndex]}
              variant="contained"
              sx={{ bgcolor: '#7c3aed', textTransform: 'none', borderRadius: '10px', '&:hover': { bgcolor: ACCENT } }}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={Object.keys(answers).length < questions.length}
              variant="contained"
              sx={{ bgcolor: GREEN, color: '#052e16', textTransform: 'none', borderRadius: '10px', fontWeight: 700, '&:hover': { bgcolor: '#22c55e' } }}
            >
              Submit Quiz
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default QuizLesson;